import React, { useState, useEffect } from 'react';
import { clientActivityAPI } from '../services/api';

interface Activity {
  id: number;
  client_id: number;
  activity_type: string;
  description: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  client_name?: string;
  company_name?: string;
  email?: string;
  user_id?: string;
  username?: string;
  entity_type?: string;
  session_id?: string;
  raw_data?: any;
}

interface Invitation {
  id: number;
  client_id: number;
  invited_by: string;
  invitation_sent_at: string;
  first_login_at?: string;
  status: 'sent' | 'accepted' | 'expired' | 'pending';
  client_name?: string;
  company_name?: string;
  email?: string;
}

const activityConfig: Record<string, { icon: string; color: string; bg: string; label: string }> = {
  login: { icon: '🔐', color: '#2563eb', bg: '#eff6ff', label: 'Login' },
  client_login: { icon: '🔐', color: '#2563eb', bg: '#eff6ff', label: 'Client Login' },
  user_login: { icon: '👤', color: '#7c3aed', bg: '#f5f3ff', label: 'User Login' },
  logout: { icon: '🚪', color: '#6b7280', bg: '#f9fafb', label: 'Logout' },
  invitation_sent: { icon: '📧', color: '#059669', bg: '#ecfdf5', label: 'Invitation Sent' },
  invitation_accepted: { icon: '✅', color: '#10b981', bg: '#ecfdf5', label: 'Accepted' },
  ticket_created: { icon: '🎫', color: '#ea580c', bg: '#fff7ed', label: 'Ticket Created' },
  ticket_viewed: { icon: '👁️', color: '#7c3aed', bg: '#f5f3ff', label: 'Ticket Viewed' },
};

const defaultConfig = { icon: '📋', color: '#2563eb', bg: '#eff6ff', label: 'Activity' };
const statusStyles: Record<string, { color: string; bg: string }> = {
  sent: { color: '#d97706', bg: '#fef3c7' },
  accepted: { color: '#059669', bg: '#d1fae5' },
  expired: { color: '#dc2626', bg: '#fee2e2' },
  pending: { color: '#d97706', bg: '#fef3c7' },
};

const ClientActivityHistory: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [tab, setTab] = useState<'timeline' | 'invitations'>('timeline');

  useEffect(() => { fetchActivityData(); }, []);

  const fetchActivityData = async () => {
    try {
      const response = await clientActivityAPI.getAll();
      setActivities(response.data.activities || []);
      setInvitations(response.data.invitations || []);
    } catch (error) { console.error('Error fetching activity data:', error); }
    finally { setLoading(false); }
  };

  const filteredActivities = filter === 'all' ? activities : activities.filter(a => a.activity_type === filter);

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #e5e7eb', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading activity...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0, color: '#111827' }}>Activity History</h1>
          <p style={{ color: '#6b7280', margin: '6px 0 0 0', fontSize: '14px' }}>Client logins, invitations, and portal activity</p>
        </div>
        <button onClick={fetchActivityData} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500, color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
          ⟳ Refresh
        </button>
      </div>

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '28px' }}>
        {[
          { label: 'Total Invitations', value: invitations.length, icon: '📧', color: '#2563eb', trend: `${invitations.filter(i => i.status === 'accepted').length} accepted` },
          { label: 'Active Users', value: invitations.filter(i => i.status === 'accepted').length, icon: '✅', color: '#059669', trend: `of ${invitations.length} invited` },
          { label: 'Total Logins', value: activities.filter(a => a.activity_type.includes('login')).length, icon: '🔐', color: '#7c3aed', trend: 'all time' },
          { label: 'Pending Invites', value: invitations.filter(i => i.status === 'sent' || i.status === 'pending').length, icon: '⏳', color: '#d97706', trend: 'awaiting response' },
        ].map((kpi, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '14px', padding: '20px', border: '1px solid #f0f0f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#111827', lineHeight: 1 }}>{kpi.value}</div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280', marginTop: '6px' }}>{kpi.label}</div>
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{kpi.trend}</div>
              </div>
              <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: `${kpi.color}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>{kpi.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #f0f0f0' }}>
        {(['timeline', 'invitations'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '12px 20px', border: 'none', background: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            color: tab === t ? '#2563eb' : '#9ca3af', borderBottom: tab === t ? '2px solid #2563eb' : '2px solid transparent', marginBottom: '-2px'
          }}>
            {t === 'timeline' ? `Timeline (${activities.length})` : `Invitations (${invitations.length})`}
          </button>
        ))}
      </div>

      {tab === 'timeline' ? (
        <>
          {/* Filter Pills */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {['all', 'login', 'client_login', 'invitation_sent', 'invitation_accepted', 'ticket_created'].map(type => {
              const conf = activityConfig[type] || defaultConfig;
              const isActive = filter === type;
              return (
                <button key={type} onClick={() => setFilter(type)} style={{
                  padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'all 0.15s',
                  background: isActive ? (type === 'all' ? '#111827' : conf.color) : '#f3f4f6',
                  color: isActive ? '#fff' : '#6b7280',
                }}>
                  {type === 'all' ? `All (${activities.length})` : `${conf.icon} ${conf.label}`}
                </button>
              );
            })}
          </div>

          {/* Timeline */}
          <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #f0f0f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
            {filteredActivities.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>📭</div>
                <p style={{ fontSize: '14px', fontWeight: 500 }}>No activity found</p>
              </div>
            ) : (
              <div>
                {filteredActivities.map((activity, idx) => {
                  const conf = activityConfig[activity.activity_type] || defaultConfig;
                  return (
                    <div key={activity.id} onClick={() => setSelectedActivity(activity)} style={{
                      padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer',
                      borderBottom: idx < filteredActivities.length - 1 ? '1px solid #f9fafb' : 'none',
                      transition: 'background 0.1s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#fafbfc'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                    >
                      {/* Icon */}
                      <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: conf.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
                        {conf.icon}
                      </div>
                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                            {activity.username || activity.client_name || 'Unknown'}
                          </span>
                          {activity.company_name && (
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>· {activity.company_name}</span>
                          )}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                          {activity.description || conf.label}
                          {activity.ip_address && <span style={{ color: '#d1d5db' }}> · {activity.ip_address}</span>}
                        </div>
                      </div>
                      {/* Time + Badge */}
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>{timeAgo(activity.created_at)}</div>
                        <span style={{ display: 'inline-block', marginTop: '4px', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, background: conf.bg, color: conf.color }}>
                          {conf.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : (
        /* Invitations Tab */
        <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #f0f0f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)', overflow: 'hidden' }}>
          {invitations.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>📧</div>
              <p style={{ fontSize: '14px', fontWeight: 500 }}>No invitations sent yet</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#fafbfc', borderBottom: '1px solid #f0f0f0' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Client</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Invited By</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sent</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>First Login</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map(inv => {
                  const s = statusStyles[inv.status] || statusStyles.pending;
                  return (
                    <tr key={inv.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 600, color: '#111827' }}>{inv.client_name || '—'}</div>
                        {inv.company_name && <div style={{ fontSize: '12px', color: '#9ca3af' }}>{inv.company_name}</div>}
                      </td>
                      <td style={{ padding: '14px 16px', color: '#374151' }}>{inv.email || '—'}</td>
                      <td style={{ padding: '14px 16px', color: '#6b7280' }}>{inv.invited_by}</td>
                      <td style={{ padding: '14px 16px', color: '#6b7280' }}>{new Date(inv.invitation_sent_at).toLocaleDateString('en-NZ')}</td>
                      <td style={{ padding: '14px 16px', color: '#6b7280' }}>{inv.first_login_at ? new Date(inv.first_login_at).toLocaleDateString('en-NZ') : '—'}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: s.bg, color: s.color }}>
                          {inv.status.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Activity Detail Modal */}
      {selectedActivity && (
        <div onClick={() => setSelectedActivity(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '16px', maxWidth: '520px', width: '100%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: (activityConfig[selectedActivity.activity_type] || defaultConfig).bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                  {(activityConfig[selectedActivity.activity_type] || defaultConfig).icon}
                </div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111827' }}>Activity Detail</h3>
              </div>
              <button onClick={() => setSelectedActivity(null)} style={{ width: '32px', height: '32px', borderRadius: '8px', border: 'none', background: '#f3f4f6', cursor: 'pointer', fontSize: '16px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div style={{ padding: '24px', display: 'grid', gap: '16px' }}>
              {[
                { label: 'Type', value: (activityConfig[selectedActivity.activity_type] || defaultConfig).label },
                { label: 'User', value: selectedActivity.username || selectedActivity.client_name || '—' },
                { label: 'Company', value: selectedActivity.company_name || '—' },
                { label: 'Time', value: new Date(selectedActivity.created_at).toLocaleString('en-NZ') },
                { label: 'Description', value: selectedActivity.description || '—' },
                ...(selectedActivity.ip_address ? [{ label: 'IP Address', value: selectedActivity.ip_address }] : []),
                ...(selectedActivity.email ? [{ label: 'Email', value: selectedActivity.email }] : []),
                ...(selectedActivity.session_id ? [{ label: 'Session', value: selectedActivity.session_id }] : []),
              ].map((field, i) => (
                <div key={i}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{field.label}</div>
                  <div style={{ fontSize: '14px', color: '#111827', fontWeight: field.label === 'Type' ? 600 : 400 }}>{field.value}</div>
                </div>
              ))}
              {selectedActivity.user_agent && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Browser</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.5 }}>{selectedActivity.user_agent}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientActivityHistory;
